import { fmt } from '@k2pos/core';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Mono, Text } from '@/components/ui/Text';
import { IconBackspace } from '@/components/icons';
import { useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Receive.module.css';

/** Common round amounts, offered before the keypad because most payments are one of these. */
const PRESETS = [2000000, 1000000, 500000];
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '000', '0', 'back'];

/** Take a full or partial payment against a balance. */
export function Receive() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customer = usePos((s) => s.customers.find((c) => c.id === id));
  const receivePayment = usePos((s) => s.receivePayment);
  const [amount, setAmount] = useState(0);

  // Resolved before the early return below: every hook must run on every render.
  const goBack = useGoBack(customer ? `/customer/${customer.id}` : '/debt');

  const balance = customer?.balance ?? 0;
  // Never let the owner record more than is owed — an overpayment is a data error.
  const capped = Math.min(amount, balance);

  const commit = useCallback(
    (amountLak: number) => receivePayment(customer?.id ?? '', amountLak),
    [receivePayment, customer?.id],
  );
  const { run, busy, error, dismiss } = useMoneyAction(commit, () =>
    navigate('/receive-done', { replace: true }),
  );

  if (!customer) {
    return <AppBar title="ບໍ່ພົບລູກຄ້າ" onBack={goBack} />;
  }

  function press(key: string) {
    setAmount((current) => {
      if (key === 'back') return Math.floor(current / 10);
      const next = Number(`${current}${key}`);
      return Number.isSafeInteger(next) ? Math.min(next, balance) : current;
    });
  }

  return (
    <>
      <AppBar title="ຮັບຊຳລະ" onBack={goBack} />

      <div className={styles.body}>
        <ErrorBanner message={error} onDismiss={dismiss} />

        <div className={styles.display}>
          <Mono
            weight={700}
            size={30}
            color={capped > 0 ? colors.text : colors.textFaint}
            className="selectable"
          >
            {fmt(capped)}
          </Mono>
          <Text size={12} color={colors.textMuted}>
            ຕິດຢູ່ {fmt(balance)} ₭
          </Text>
        </div>

        <div className={styles.presets}>
          <Preset label="ຈ່າຍໝົດ" sub={fmt(balance)} onPress={() => setAmount(balance)} />
          {PRESETS.map((value) => (
            <Preset
              key={value}
              label={fmt(value)}
              sub="₭"
              onPress={() => setAmount(Math.min(value, balance))}
            />
          ))}
        </div>

        <div className={styles.keypad}>
          {KEYS.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={key === 'back' ? 'ລຶບ' : key}
              onClick={() => press(key)}
              className={styles.key}
            >
              {key === 'back' ? (
                <IconBackspace size={20} />
              ) : (
                <Mono weight={700} size={18} color={colors.text}>
                  {key}
                </Mono>
              )}
            </button>
          ))}
        </div>

        <Button
          label={busy ? 'ກຳລັງບັນທຶກ...' : 'ຢືນຢັນ ຮັບເງິນ'}
          variant="accent"
          disabled={capped <= 0 || busy}
          onPress={() => run(capped)}
        />
      </div>
    </>
  );
}

function Preset({ label, sub, onPress }: { label: string; sub: string; onPress: () => void }) {
  return (
    <button type="button" onClick={onPress} className={styles.preset}>
      <Text weight={800} size={13} color={colors.navy}>
        {label}
      </Text>
      <Mono size={10} color={colors.textMuted}>
        {sub}
      </Mono>
    </button>
  );
}
